import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from '@fs/zion-axios'
import { updatePlaylist as updatePlaylistApi, addMultipleItemsToPlaylist, removePlaylistItem } from '../../api'
import notifySentry from '../../lib/helpers/sentryNotifier'
import type { Playlist, PlaylistsByItem } from '../../api/rootstech/playlist/playlist.types'

type PlaylistsCache = { data: { results: Playlist[] } }
type PlaylistsByItemsCache = { data: PlaylistsByItem }

const withAddedItems = (playlist: Playlist, sessionIds: string[], addedAt: string): Playlist => {
  const existingItems = playlist.items ?? []
  const newItems = sessionIds
    .filter((id) => !existingItems.some((item) => item.id === id))
    .map((id) => ({ id, addedAt }))
  return { ...playlist, items: [...existingItems, ...newItems], size: (playlist.size ?? 0) + newItems.length }
}

const withRemovedItem = (playlist: Playlist, sessionId: string): Playlist => {
  const newItems = (playlist.items ?? []).filter((item) => item.id !== sessionId)
  return { ...playlist, items: newItems, size: newItems.length }
}

const addPlaylistToByItems = (
  data: PlaylistsByItem,
  sessionIds: string[],
  playlistId: string,
  playlist: Playlist
): PlaylistsByItem => {
  const newData = { ...data }
  sessionIds.forEach((sessionId) => {
    if (!newData[sessionId]) {
      newData[sessionId] = { results: [playlist] }
    } else if (!newData[sessionId].results.some((p) => p.id === playlistId)) {
      newData[sessionId] = { results: [...newData[sessionId].results, playlist] }
    }
  })
  return newData
}

export type UpdatePlaylistParams = {
  id: string
  name?: string
  description?: string
  addItems?: string[]
  removeItems?: string[]
}

type UsePlaylistUpdate = Omit<UseMutationResult<void, AxiosError, UpdatePlaylistParams>, 'mutateAsync'> & {
  updatePlaylist: (params: UpdatePlaylistParams) => Promise<void>
}

const useUpdatePlaylistDetails = (): UseMutationResult<
  void,
  AxiosError,
  { playlistId: string; name?: string; description?: string }
> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; name?: string; description?: string }>({
    mutationFn: ({ playlistId, name, description }) => {
      const cache = queryClient.getQueryData<PlaylistsCache>(['playlists'])
      const existing = cache?.data.results.find((p) => p.id === playlistId)
      return updatePlaylistApi({
        playlistId,
        data: {
          name: name ?? existing?.name ?? '',
          description: description ?? existing?.description ?? '',
        },
      })
    },
    onSuccess: (_, { playlistId, name, description }) => {
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.map((p) =>
              p.id === playlistId
                ? { ...p, ...(name !== undefined && { name }), ...(description !== undefined && { description }) }
                : p
            ),
          },
        }
      })
      showStatusOverlay({
        message: t('playlists.alerts.details-updated', 'Playlist details updated.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.details.update-error',
          "We're sorry, but there was an issue updating the playlist details. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

const useAddPlaylistItems = (): UseMutationResult<void, AxiosError, { playlistId: string; sessionIds: string[] }> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; sessionIds: string[] }>({
    mutationFn: ({ playlistId, sessionIds }) => addMultipleItemsToPlaylist({ playlistId, sessionIds }),
    onSuccess: (_, { playlistId, sessionIds }) => {
      const now = new Date().toISOString()

      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.map((p) => (p.id === playlistId ? withAddedItems(p, sessionIds, now) : p)),
          },
        }
      })

      const playlistsCache = queryClient.getQueryData<PlaylistsCache>(['playlists'])
      const updatedPlaylist = playlistsCache?.data.results.find((p) => p.id === playlistId)
      if (updatedPlaylist) {
        queryClient.setQueriesData<PlaylistsByItemsCache>({ queryKey: ['playlistsByItems'] }, (old) => {
          if (!old) return old
          return { ...old, data: addPlaylistToByItems(old.data, sessionIds, playlistId, updatedPlaylist) }
        })
      }

      showStatusOverlay({
        message: t('playlists.alerts.item-added', 'Item added to playlist.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.items.add-error',
          "We're sorry, but there was an issue adding the item to your playlist. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

const useRemovePlaylistItem = (): UseMutationResult<void, AxiosError, { playlistId: string; sessionId: string }> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; sessionId: string }>({
    mutationFn: ({ playlistId, sessionId }) => removePlaylistItem({ playlistId, sessionId }),
    onSuccess: (_, { playlistId, sessionId }) => {
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.map((p) => (p.id === playlistId ? withRemovedItem(p, sessionId) : p)),
          },
        }
      })

      queryClient.setQueriesData<PlaylistsByItemsCache>({ queryKey: ['playlistsByItems'] }, (old) => {
        if (!old?.data[sessionId]) return old
        return {
          ...old,
          data: {
            ...old.data,
            [sessionId]: { results: old.data[sessionId].results.filter((p) => p.id !== playlistId) },
          },
        }
      })

      showStatusOverlay({
        message: t('playlists.alerts.item-removed', 'Item removed from playlist.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.items.remove-error',
          "We're sorry, but there was an issue removing the item from your playlist. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

export const usePlaylistUpdate = (): UsePlaylistUpdate => {
  const updateDetailsMutation = useUpdatePlaylistDetails()
  const addItemsMutation = useAddPlaylistItems()
  const removeItemMutation = useRemovePlaylistItem()

  const { mutateAsync: updatePlaylist, ...rest } = useMutation<void, AxiosError, UpdatePlaylistParams>({
    mutationFn: async ({ id: playlistId, name, description, addItems, removeItems }) => {
      const ops: Promise<void>[] = []

      if (name !== undefined || description !== undefined) {
        ops.push(updateDetailsMutation.mutateAsync({ playlistId, name, description }))
      }
      if (addItems?.length) {
        ops.push(addItemsMutation.mutateAsync({ playlistId, sessionIds: addItems }))
      }
      if (removeItems?.length) {
        ops.push(...removeItems.map((sessionId) => removeItemMutation.mutateAsync({ playlistId, sessionId })))
      }

      await Promise.all(ops)
    },
  })

  return { updatePlaylist, ...rest }
}
